import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { toast } from '../lib/toast.js';
import { useAuth } from '../features/auth/AuthProvider.js';
import { useRolePerms } from '../features/auth/useRolePerms.js';
import { CertNotifyModal } from '../components/domain/CertNotifyModal.js';
import { waLink } from '../lib/wa.js';
import { RealMap, positionTooltip, type RealMapPin } from '../components/primitives/RealMap.js';

interface HomeCert { id: string; type: string; expiry: string | null; status: 'valid' | 'expiring' | 'expired'; docRef?: string | null; docUrl?: string | null }
interface HomeWorker { id: string; name: string; role: string; roles?: string[]; agencyCode: string | null; payrollRef: string | null; status: string; certs: HomeCert[] }
interface HomeDeployment { id: string; workerId: string; projectId: string; projectCode?: string | null; projectName: string; projectAddress: string | null; projectPostcode: string | null; startDate: string; startTime: string; endDate: string | null; status: string; meet: string | null; supervisorName: string | null; supervisorPhone: string | null }
interface HomeProject { id: string; code: string; name: string; address: string; postcode: string; status: string }
interface HomeSignIn { id: string; workerId: string; type: 'in' | 'out'; timestamp: string; projectName: string | null; withinGeofence: boolean | null }
interface HomeDoc { id: string; name: string; createdAt: string; recipients: { id: string; status: string; signedAt: string | null }[] }

const mapsLink = (postcode: string) => `https://www.google.com/maps/search/${encodeURIComponent(postcode)}`;
const fmtDate = (iso: string | null) => iso ? new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const fmtWhen = (iso: string) => new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });

function WorkerDashboard({ workerId, userName, isSupervisor }: { workerId: string | null; userName: string; isSupervisor: boolean }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [sharePhone, setSharePhone] = useState('');
  const today = new Date().toISOString().slice(0, 10);

  const { data: me } = useQuery({
    queryKey: ['workers', workerId, 'self'],
    queryFn: () => api.get<HomeWorker>(`/workers/${workerId}`).then((r) => r.data),
    enabled: !!workerId,
  });
  const { data: myDocs = [] } = useQuery({
    queryKey: ['shared-docs'],
    queryFn: () => api.get<HomeDoc[]>('/shared-docs').then((r) => r.data),
  });
  const { data: signals = [] } = useQuery({
    queryKey: ['availability-signals', 'recent-for', workerId],
    queryFn: () => api.get<{ id: string; signaledAt: string }[]>(`/availability-signals/recent-for/${workerId}`).then((r) => r.data),
    enabled: !!workerId,
  });
  const { data: deployments = [] } = useQuery({
    queryKey: ['deployments'],
    queryFn: () => api.get<HomeDeployment[]>('/deployments').then((r) => r.data),
  });
  const { data: signins = [] } = useQuery({
    queryKey: ['signin-records'],
    queryFn: () => api.get<HomeSignIn[]>('/signin-records').then((r) => r.data),
  });
  const { data: sites = [] } = useQuery({
    queryKey: ['projects'],
    queryFn: () => api.get<HomeProject[]>('/projects').then((r) => r.data),
    enabled: isSupervisor,
  });

  const flipUnavailable = useMutation({
    mutationFn: async () => api.patch(`/workers/${workerId}`, { status: 'unavailable' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['workers', workerId, 'self'] });
      toast.success('Marked Unavailable', "We've let the office know.");
    },
    onError: () => toast.error('Could not update', 'Try again in a moment.'),
  });

  const sign = useMutation({
    mutationFn: async ({ projectId, type }: { projectId: string; type: 'in' | 'out' }) => {
      let lat = 0, lng = 0, accuracy = 0, gps = false;
      try {
        const pos = await new Promise<GeolocationPosition>((res, rej) => navigator.geolocation.getCurrentPosition(res, rej, { timeout: 8000, enableHighAccuracy: true }));
        lat = pos.coords.latitude; lng = pos.coords.longitude; accuracy = pos.coords.accuracy; gps = true;
      } catch {
        gps = false;
      }
      const rec = await api.post<HomeSignIn>('/signin-records', { workerId, projectId, type, lat, lng, accuracy, device: navigator.userAgent.slice(0, 200) }).then((r) => r.data);
      return { rec, gps };
    },
    onSuccess: ({ rec, gps }, v) => {
      qc.invalidateQueries({ queryKey: ['signin-records'] });
      const inside = gps ? rec?.withinGeofence : null;
      const tail = inside === true ? '✓ within geofence' : inside === false ? '⚠ outside geofence' : 'recorded without a location — allow location access next time';
      const title = v.type === 'in' ? '✅ Signed in' : '🚪 Signed out';
      if (inside === false) toast.warn(title, tail);
      else toast.success(title, tail);
    },
    onError: () => toast.error('Sign failed', 'Try again in a moment.'),
  });

  // The DNU status is internal — never show it to the worker.
  const displayedStatus = me?.status === 'dnu' ? 'unavailable' : me?.status;
  const statusLabel: Record<string, string> = { available: 'Available', deployed: 'Deployed', unavailable: 'Unavailable' };
  const statusColor: Record<string, string> = { available: '#22c55e', deployed: '#E8A427', unavailable: '#ef4444' };

  const certs = me?.certs ?? [];
  const expired = certs.filter((c) => c.status === 'expired');
  const expiring = certs.filter((c) => c.status === 'expiring');
  const valid = certs.filter((c) => c.status === 'valid');
  const recentSignal = signals[0];
  const docRows = myDocs.map((d) => ({ id: d.id, name: d.name, createdAt: d.createdAt, status: d.recipients[0]?.status ?? 'pending', signedAt: d.recipients[0]?.signedAt ?? null }));
  const docsToSign = docRows.filter((d) => d.status === 'pending' || d.status === 'viewed');
  const docsSigned = docRows.filter((d) => d.status === 'signed');
  const current = workerId
    ? deployments.find((d) => d.workerId === workerId && d.status === 'active' && d.startDate.slice(0, 10) <= today && (!d.endDate || d.endDate.slice(0, 10) >= today)) ?? null
    : null;
  const lastSign = workerId ? signins.find((r) => r.workerId === workerId) ?? null : null;
  const nextSign: 'in' | 'out' = lastSign?.type === 'in' ? 'out' : 'in';
  const trades = me ? Array.from(new Set([me.role, ...(me.roles ?? [])].filter(Boolean))).join(', ') : '';
  const activeOnSite = (projectId: string) => deployments.filter((d) => d.projectId === projectId && d.status === 'active').length;

  const shareWhatsApp = () => {
    if (!me) return;
    const phone = sharePhone.trim();
    if (!phone) { toast.warn('Phone number needed', 'Type the number to send your training record to.'); return; }
    const certLines = certs.length ? certs.map((c) => `- ${c.type} (${fmtDate(c.expiry)}) — ${c.status === 'valid' ? 'Valid' : c.status === 'expiring' ? 'Expiring' : 'Expired'}`).join('\n') : 'None on record';
    window.open(waLink(phone, `Training Record — ${me.name}\nRole: ${trades}\n\nCertificates:\n${certLines}\n\n— CLS Construction Logistics Services`), '_blank');
  };

  return (
    <div className="page" id="page-dashboard">
      <div className="hbar">
        <div>
          <div className="ph">Hi {userName.split(' ')[0]}</div>
          <div className="ps">{isSupervisor ? 'Your sites, your workers and your own record at a glance.' : 'Your training, availability and deployments at a glance.'}</div>
        </div>
      </div>

      <div className="g2" style={{ marginBottom: 14 }}>
        {me ? (
          <div className="card" id="my-profile">
            <div className="sec-title" style={{ marginBottom: 10 }}>👤 My profile</div>
            <div className="dp-row"><span className="dp-key">Name</span><span className="dp-val">{me.name}</span></div>
            <div className="dp-row"><span className="dp-key">Trade</span><span className="dp-val">{trades || '—'}</span></div>
            <div className="dp-row"><span className="dp-key">Agency</span><span className="dp-val">{me.agencyCode ?? 'CLS'}</span></div>
            <div className="dp-row"><span className="dp-key">Payroll ID</span><span className="dp-val">{me.payrollRef ?? '—'}</span></div>
            <div style={{ marginTop: 10, fontSize: 10, color: 'var(--muted)' }}>
              These details are read-only. To change them, complete the DPA questions with Octopaye and the office will update your record.
            </div>
          </div>
        ) : null}

        {workerId ? (
          <div className="card" id="my-site">
            <div className="sec-title" style={{ marginBottom: 10 }}>📍 My current site</div>
            {current ? (
              <>
                <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 14 }}>{current.projectCode ? `${current.projectCode} — ` : ''}{current.projectName}</div>
                <div style={{ fontSize: 12, marginTop: 4 }}>{current.projectAddress ?? '—'}</div>
                {current.projectPostcode ? (
                  <a href={mapsLink(current.projectPostcode)} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)', fontFamily: 'Syne, sans-serif', fontWeight: 700, textDecoration: 'none', fontSize: 12 }}>📍 {current.projectPostcode} · open in Google Maps ↗</a>
                ) : null}
                <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 8 }}>
                  Starts {fmtDate(current.startDate)} at {current.startTime}{current.endDate ? ` · until ${fmtDate(current.endDate)}` : ''}
                  {current.meet ? <><br />Meet: {current.meet}</> : null}
                  {current.supervisorName ? <><br />Site supervisor: {current.supervisorName}{current.supervisorPhone ? <> · <a href={`tel:${current.supervisorPhone}`} style={{ color: 'var(--text)' }}>{current.supervisorPhone}</a></> : null}</> : null}
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                  <button className="btn btn-primary" id="home-sign-in" style={{ flex: 1, justifyContent: 'center', padding: 12, fontSize: 13, ...(nextSign === 'in' ? { outline: '2px solid var(--live)' } : null) }} disabled={sign.isPending} onClick={() => sign.mutate({ projectId: current.projectId, type: 'in' })}>✅ Sign In</button>
                  <button className="btn btn-ghost" id="home-sign-out" style={{ flex: 1, justifyContent: 'center', padding: 12, fontSize: 13, ...(nextSign === 'out' ? { outline: '2px solid var(--warn)' } : null) }} disabled={sign.isPending} onClick={() => sign.mutate({ projectId: current.projectId, type: 'out' })}>🚪 Sign Out</button>
                </div>
                <div style={{ marginTop: 8, fontSize: 10, color: 'var(--muted)' }}>
                  {lastSign ? `Last record: ${lastSign.type === 'in' ? 'signed in' : 'signed out'} ${fmtWhen(lastSign.timestamp)}${lastSign.projectName ? ` at ${lastSign.projectName}` : ''}` : 'No sign-in records yet.'}
                </div>
              </>
            ) : (
              <div style={{ fontSize: 12, color: 'var(--muted)', padding: '10px 0' }}>
                Not currently deployed. You can only sign in to a site you've been deployed to — if you're meant to be on site, contact the office.
              </div>
            )}
          </div>
        ) : null}

        {isSupervisor ? (
          <div className="card" id="my-sites">
            <div className="sec-title" style={{ marginBottom: 10 }}>🏗 My sites</div>
            {sites.length === 0 ? (
              <div style={{ fontSize: 12, color: 'var(--muted)', textAlign: 'center', padding: 14 }}>No sites linked to your account yet — ask the office.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {sites.map((p) => (
                  <div key={p.id} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6, padding: '8px 10px', fontSize: 11 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                      <span style={{ fontWeight: 600 }}>{p.code} — {p.name}</span>
                      <span style={{ color: 'var(--muted)' }}>{activeOnSite(p.id)} on site</span>
                    </div>
                    <div style={{ color: 'var(--muted)', marginTop: 2 }}>{p.address}</div>
                    <a href={mapsLink(p.postcode)} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)', fontFamily: 'Syne, sans-serif', fontWeight: 700, textDecoration: 'none', fontSize: 11 }}>📍 {p.postcode} ↗</a>
                  </div>
                ))}
              </div>
            )}
            <button className="btn btn-ghost btn-xs" style={{ marginTop: 10 }} onClick={() => navigate('/workers')}>View my workers →</button>
          </div>
        ) : null}

        {workerId ? (
          <div className="card">
            <div className="sec-title" style={{ marginBottom: 10 }}>📡 My availability</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
              <div style={{ width: 14, height: 14, borderRadius: '50%', background: statusColor[displayedStatus ?? ''] ?? 'var(--muted)' }} />
              <div>
                <div style={{ fontSize: 13, fontWeight: 700 }}>{statusLabel[displayedStatus ?? ''] ?? '—'}</div>
                {recentSignal ? (
                  <div style={{ fontSize: 10, color: 'var(--muted)' }}>You said you were available {fmtWhen(recentSignal.signaledAt)}</div>
                ) : null}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-ghost" style={{ flex: 1, justifyContent: 'center', padding: 12, fontSize: 13, color: '#ef4444', borderColor: '#ef4444' }} onClick={() => flipUnavailable.mutate()} disabled={flipUnavailable.isPending || displayedStatus === 'unavailable'}>🔴 I'm unavailable</button>
            </div>
            <div style={{ marginTop: 10, fontSize: 10, color: 'var(--muted)' }}>
              "I'm unavailable" pauses you until you tell us otherwise.
            </div>
          </div>
        ) : null}

        <div className="card" id="my-documents">
          <div className="sec-title" style={{ marginBottom: 10 }}>✍ My documents</div>
          {docRows.length === 0 ? (
            <div style={{ fontSize: 12, color: 'var(--muted)', textAlign: 'center', padding: 14 }}>No documents have been shared with you yet.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {docsToSign.map((d) => (
                <div key={d.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, background: 'rgba(245,158,11,.08)', border: '1px solid rgba(245,158,11,.3)', borderRadius: 6, padding: '8px 10px', fontSize: 11 }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.name}</div>
                    <div style={{ fontSize: 10, color: 'var(--muted)' }}>Shared {fmtDate(d.createdAt)} · awaiting your signature</div>
                  </div>
                  <button className="btn btn-primary btn-xs" onClick={() => navigate('/docsign')}>✍ Sign</button>
                </div>
              ))}
              {docsSigned.map((d) => (
                <div key={d.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, background: 'rgba(34,197,94,.08)', border: '1px solid rgba(34,197,94,.3)', borderRadius: 6, padding: '8px 10px', fontSize: 11 }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.name}</div>
                    <div style={{ fontSize: 10, color: 'var(--muted)' }}>Signed {fmtDate(d.signedAt)}</div>
                  </div>
                  <span style={{ color: '#22c55e', fontWeight: 700 }}>✓ SIGNED</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {me ? (
          <div className="card" id="my-certs">
            <div className="sec-title" style={{ marginBottom: 10 }}>🎓 My training certificates</div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
              <div className="kpi" style={{ flex: 1, minWidth: 80 }}><div className="kpi-val" style={{ color: '#22c55e' }}>{valid.length}</div><div className="kpi-lbl">Valid</div></div>
              <div className="kpi" style={{ flex: 1, minWidth: 80 }}><div className="kpi-val" style={{ color: '#F59E0B' }}>{expiring.length}</div><div className="kpi-lbl">Expiring</div></div>
              <div className="kpi" style={{ flex: 1, minWidth: 80 }}><div className="kpi-val" style={{ color: '#EF4444' }}>{expired.length}</div><div className="kpi-lbl">Expired</div></div>
            </div>
            {certs.length === 0 ? (
              <div style={{ fontSize: 12, color: 'var(--muted)', textAlign: 'center', padding: 14 }}>No certificates on record yet.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {[...expired, ...expiring, ...valid].map((c) => {
                  const tone = c.status === 'expired' ? '#ef4444' : c.status === 'expiring' ? '#F59E0B' : '#22c55e';
                  return (
                    <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, background: c.status === 'valid' ? 'var(--surface)' : `${tone}14`, border: `1px solid ${c.status === 'valid' ? 'var(--border)' : `${tone}4d`}`, borderRadius: 6, padding: '8px 10px', fontSize: 11 }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 600 }}>{c.type}</div>
                        <div style={{ fontSize: 10, color: tone, fontWeight: 700 }}>{c.status === 'expired' ? 'EXPIRED' : c.status === 'expiring' ? 'EXPIRING' : 'Valid until'} {fmtDate(c.expiry)}</div>
                      </div>
                      {c.docUrl ? <a className="btn btn-ghost btn-xs" href={c.docUrl} target="_blank" rel="noopener noreferrer">📄 Open</a> : <span style={{ fontSize: 10, color: 'var(--faint)' }}>no copy on file</span>}
                    </div>
                  );
                })}
              </div>
            )}
            <div style={{ marginTop: 12, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <a className="btn btn-ghost btn-sm" href={`/api/reports/training/worker/${me.id}/share.html`} target="_blank" rel="noopener noreferrer">🖨 Open my training record</a>
              <a className="btn btn-ghost btn-sm" href={`/api/reports/training/worker/${me.id}/share.pdf`} target="_blank" rel="noopener noreferrer">⬇ PDF</a>
            </div>
            <div style={{ marginTop: 8, display: 'flex', gap: 6 }}>
              <input type="tel" className="form-input" id="share-phone" placeholder="Phone number to share with" value={sharePhone} onChange={(e) => setSharePhone(e.target.value)} style={{ flex: 1 }} />
              <button className="btn btn-ghost btn-sm" style={{ color: '#25D366', borderColor: '#25D366' }} onClick={shareWhatsApp}>WhatsApp</button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

interface Kpis {
  activeWorkforce: number;
  onSiteToday: number;
  deployed: number;
  available: number;
  unavailable: number;
  liveProjects: number;
  upcomingStarts: number;
  expiringCerts: number;
  expiredCerts: number;
  validCerts: number;
  totalCerts: number;
  complianceRatio: number;
  activeDeployments: number;
  nonCompliantSuppliers: number;
  dnaWeek: number;
  broadcasts7d: number;
  recentCerts: number;
  visaExpiring: number;
  alert: { expired: number; expiring: number; alertText: string };
}

interface VacancyCandidate {
  workerId: string;
  workerName: string;
  workerRole?: string;
  rank?: number;
  score?: number;
  status?: string;
}

interface Vacancy {
  id: string;
  code: string | null;
  projectId: string;
  projectCode?: string | null;
  projectName?: string;
  role: string;
  startDate: string;
  duration: string;
  requesterName: string | null;
  candidates?: VacancyCandidate[];
}

interface ProjectMap {
  id: string;
  code: string;
  name: string;
  status: string;
  lat: number | null;
  lng: number | null;
  postcode: string | null;
  address: string | null;
}

interface Overview {
  kpis: Kpis;
  activeVacancies: { open: Vacancy[]; filled: Vacancy[] };
  projectsMap: ProjectMap[];
}

interface Worker {
  id: string;
  name: string;
  role: string;
  status: string;
  phone?: string;
  photo?: string | null;
  certs: Cert[];
}
interface Cert { id: string; type: string; expiry: string | null; status: 'valid' | 'expiring' | 'expired'; workerId: string; }
interface Project { id: string; code: string; name: string; status: string; postcode: string | null; lat: number | null; lng: number | null; }
interface Customer { id: string; name: string; type: string; projects: Array<{ id: string; code: string; status: string }>; }
interface Supplier { id: string; name: string; compliance: 'compliant' | 'non-compliant' | 'pending'; category?: string; }
interface Deployment { id: string; workerId: string; projectId: string; status: string; startDate: string; endDate: string | null; arrivalStatus?: string | null; }
interface AvailabilitySignal { id: string; workerId: string; workerName: string; workerRole: string | null; signaledAt: string; wasDeployedOn: string[]; }
interface SignInRecord { id: string; workerId: string; projectId: string | null; type: 'in' | 'out'; timestamp: string; }
interface ActivityEntry { id: string; icon: string; text: string; time: string; userName: string | null; }

// ═══ Helpers (mirror prototype's JS helpers) ═══════════════════
function cBdg(status: 'valid' | 'expiring' | 'expired'): JSX.Element {
  const map = {
    valid: { bg: 'rgba(34,197,94,.15)', fg: '#22C55E', lbl: 'Valid' },
    expiring: { bg: 'rgba(245,158,11,.18)', fg: '#F59E0B', lbl: 'Expiring' },
    expired: { bg: 'rgba(239,68,68,.18)', fg: '#EF4444', lbl: 'Expired' },
  }[status];
  return (
    <span style={{ background: map.bg, color: map.fg, borderRadius: 999, padding: '1px 7px', fontSize: 9, fontWeight: 700, fontFamily: 'Syne, sans-serif', textTransform: 'uppercase', letterSpacing: '.5px' }}>
      {map.lbl}
    </span>
  );
}

function compBdg(compliance: 'compliant' | 'non-compliant' | 'pending'): JSX.Element {
  const map = {
    'compliant': { bg: 'rgba(34,197,94,.15)', fg: '#22C55E', lbl: '✓' },
    'non-compliant': { bg: 'rgba(239,68,68,.18)', fg: '#EF4444', lbl: '✕' },
    'pending': { bg: 'rgba(245,158,11,.18)', fg: '#F59E0B', lbl: '…' },
  }[compliance];
  return (
    <span style={{ background: map.bg, color: map.fg, borderRadius: 999, padding: '1px 6px', fontSize: 9, fontWeight: 700, fontFamily: 'Syne, sans-serif' }}>
      {map.lbl}
    </span>
  );
}

function initials(name: string): string {
  return name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase();
}

function avEl(name: string, size = 24, font = 9): JSX.Element {
  return (
    <div style={{ width: size, height: size, minWidth: size, borderRadius: '50%', background: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: font, fontFamily: 'Syne, sans-serif', fontWeight: 800, color: '#0B0F1A' }}>
      {initials(name)}
    </div>
  );
}

function relTime(d: Date): string {
  const diff = Date.now() - d.getTime();
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  if (days < 30) return `${days}d ago`;
  return d.toLocaleDateString();
}

function availSignalAge(iso: string): string {
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  return `${d}d`;
}

function certDaysLeft(expiry: string | null): number {
  if (!expiry) return Infinity;
  const d = new Date(expiry);
  return Math.ceil((d.getTime() - Date.now()) / 86_400_000);
}

const PROJECT_COLOURS = [
  { bg: '#1d4ed8', light: 'rgba(29,78,216,.18)', text: '#93c5fd' },
  { bg: '#7c3aed', light: 'rgba(124,58,237,.18)', text: '#c4b5fd' },
  { bg: '#0891b2', light: 'rgba(8,145,178,.18)', text: '#67e8f9' },
  { bg: '#059669', light: 'rgba(5,150,105,.18)', text: '#6ee7b7' },
  { bg: '#d97706', light: 'rgba(217,119,6,.18)', text: '#fcd34d' },
  { bg: '#dc2626', light: 'rgba(220,38,38,.18)', text: '#fca5a5' },
  { bg: '#0d9488', light: 'rgba(13,148,136,.18)', text: '#5eead4' },
  { bg: '#b45309', light: 'rgba(180,83,9,.18)', text: '#fbbf24' },
  { bg: '#be185d', light: 'rgba(190,24,93,.18)', text: '#f9a8d4' },
  { bg: '#4f46e5', light: 'rgba(79,70,229,.18)', text: '#a5b4fc' },
];

// ═══ Dashboard ═════════════════════════════════════════════════
export function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isSiteUser, isSupervisor, canCreateVacancy } = useRolePerms();
  if (isSiteUser && user) {
    return <WorkerDashboard workerId={user.workerId ?? null} userName={user.name} isSupervisor={isSupervisor} />;
  }
  const [calYear, setCalYear] = useState<number>(new Date().getFullYear());
  const [calMonth, setCalMonth] = useState<number>(new Date().getMonth());
  const mapBodyRef = useRef<HTMLDivElement>(null);
  const [hoverPin, setHoverPin] = useState<{ id: string; leftPx: number; topPx: number } | null>(null);
  const [notifyOpen, setNotifyOpen] = useState(false);

  const { data: overview } = useQuery({
    queryKey: ['dashboard', 'overview'],
    queryFn: () => api.get<Overview>('/dashboard/overview').then((r) => r.data),
    refetchInterval: 30_000,
  });
  const { data: workers = [] } = useQuery({ queryKey: ['workers'], queryFn: () => api.get<Worker[]>('/workers').then((r) => r.data) });
  const { data: projects = [] } = useQuery({ queryKey: ['projects'], queryFn: () => api.get<Project[]>('/projects').then((r) => r.data) });
  const { data: customers = [] } = useQuery({ queryKey: ['customers'], queryFn: () => api.get<Customer[]>('/customers').then((r) => r.data) });
  const { data: suppliers = [] } = useQuery({ queryKey: ['suppliers'], queryFn: () => api.get<Supplier[]>('/suppliers').then((r) => r.data) });
  const { data: deployments = [] } = useQuery({ queryKey: ['deployments'], queryFn: () => api.get<Deployment[]>('/deployments').then((r) => r.data) });
  const { data: signals = [] } = useQuery({ queryKey: ['availability-signals'], queryFn: () => api.get<AvailabilitySignal[]>('/availability-signals').then((r) => r.data) });
  const { data: signinRecords = [] } = useQuery({ queryKey: ['signin-records'], queryFn: () => api.get<SignInRecord[]>('/signin-records').then((r) => r.data) });
  const { data: activity = [] } = useQuery({ queryKey: ['activity-log'], queryFn: () => api.get<ActivityEntry[]>('/activity-log?limit=10').then((r) => r.data) });

  const kpis = overview?.kpis;
  const activeVacancies = overview?.activeVacancies;
  const projectsMap = overview?.projectsMap ?? [];

  const expired = kpis?.alert.expired ?? 0;
  const expiring = kpis?.alert.expiring ?? 0;

  // KPI tiles (prototype: Live Projects / Workers Available / Active Deployments / Compliance Issues)
  const kpiTiles = [
    { label: 'Live Projects', val: kpis?.liveProjects ?? 0, sub: `${projects.length} total · click to view`, color: '#22C55E', go: () => navigate('/projects') },
    { label: 'Workers Available', val: kpis?.available ?? 0, sub: `${kpis?.deployed ?? 0} deployed · ${kpis?.unavailable ?? 0} unavailable`, color: '#E8A427', go: () => navigate('/workers?status=available') },
    { label: 'Active Deployments', val: kpis?.activeDeployments ?? 0, sub: 'click to view records', color: '#3B82F6', go: () => navigate('/deploylog') },
    { label: 'Compliance Issues', val: kpis?.nonCompliantSuppliers ?? 0, sub: 'click to filter suppliers', color: '#EF4444', go: () => navigate('/suppliers?compliance=non-compliant') },
  ];

  // Map pins for the real Leaflet map
  const mapPins = projectsMap.filter((p): p is ProjectMap & { lat: number; lng: number } => p.lat != null && p.lng != null);
  const realMapPins: RealMapPin[] = mapPins.map((p) => ({
    id: p.id,
    lat: p.lat,
    lng: p.lng,
    iconHtml: `<div class="mpin-${p.status === 'live' ? 'live' : 'completed'}" style="display:flex;flex-direction:column;align-items:center"><div class="mpin-dot"></div><div class="mpin-stem"></div></div>`,
    iconSize: [18, 24],
    iconAnchor: [9, 24],
  }));
  const hoverProject = hoverPin ? mapPins.find((x) => x.id === hoverPin.id) : null;

  // Live projects
  const liveProjects = projects.filter((p) => p.status === 'live');

  // Cert alerts (top 6 non-valid)
  const allCerts = workers.flatMap((w) => w.certs.map((c) => ({ ...c, wname: w.name, wid: w.id })));
  const certAlerts = allCerts.filter((c) => c.status !== 'valid').slice(0, 6);

  // Newly available (last 7 days)
  const recentSignals = signals
    .filter((s) => Date.now() - new Date(s.signaledAt).getTime() < 7 * 86_400_000)
    .slice(0, 6);

  // Deploy summary
  const activeDepsByProject: Record<string, number> = {};
  deployments.filter((d) => d.status === 'active').forEach((d) => {
    activeDepsByProject[d.projectId] = (activeDepsByProject[d.projectId] ?? 0) + 1;
  });

  // Expiry forecast (certs expiring within 30 days, sorted by days left)
  const expiryForecast = allCerts
    .map((c) => ({ ...c, daysLeft: certDaysLeft(c.expiry) }))
    .filter((c) => c.status !== 'valid' && c.daysLeft >= 0 && c.daysLeft <= 30)
    .sort((a, b) => a.daysLeft - b.daysLeft);

  // ═══ Calendar ════════════════════════════════════════════════
  const daysInMonth = new Date(calYear, calMonth + 1, 0).getDate();
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dayNames = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
  const pcMap: Record<string, typeof PROJECT_COLOURS[number]> = {};
  let pcIdx = 0;
  const projectColour = (pid: string) => {
    if (!pcMap[pid]) {
      pcMap[pid] = PROJECT_COLOURS[pcIdx % PROJECT_COLOURS.length]!;
      pcIdx++;
    }
    return pcMap[pid]!;
  };
  liveProjects.forEach((p) => projectColour(p.id));

  const calPrev = () => {
    let y = calYear, m = calMonth - 1;
    if (m < 0) { m = 11; y--; }
    setCalMonth(m); setCalYear(y);
  };
  const calNext = () => {
    let y = calYear, m = calMonth + 1;
    if (m > 11) { m = 0; y++; }
    setCalMonth(m); setCalYear(y);
  };
  const calLabel = new Date(calYear, calMonth, 1).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });

  const dashDate = 'Construction Logistics Services · ' + new Date().toLocaleDateString('en-GB', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

  return (
    <div className="page active" id="page-dashboard">
      <div className="hbar">
        <div>
          <div className="ph">Welcome back, CLS</div>
          <div className="ps" id="dash-date">{dashDate}</div>
        </div>
      </div>

      <div id="dash-alerts" style={{ marginBottom: 12 }}>
        {(expired > 0 || expiring > 0) ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '7px 13px', background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 11 }}>
            {expired > 0 ? (
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ color: 'var(--danger)' }}>⚠</span>
                <strong style={{ color: 'var(--danger)' }}>{expired} expired certificate{expired !== 1 ? 's' : ''}</strong>
                <span style={{ color: 'var(--muted)' }}>need attention</span>
                <button className="btn btn-ghost btn-xs" onClick={() => navigate('/training')}>View →</button>
              </span>
            ) : null}
            {expired > 0 && expiring > 0 ? <span style={{ color: 'var(--border)', fontSize: 16, lineHeight: 1 }}>|</span> : null}
            {expiring > 0 ? (
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ color: 'var(--warn)' }}>⏱</span>
                <strong style={{ color: 'var(--warn)' }}>{expiring} expiring soon</strong>
                <button className="btn btn-ghost btn-xs" onClick={() => setNotifyOpen(true)}>Notify →</button>
              </span>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="g4" style={{ marginBottom: 16 }} id="dash-kpis">
        {kpiTiles.map((k) => (
          <div key={k.label} className="kpi" style={{ borderTopColor: k.color, cursor: 'pointer' }} onClick={k.go} title="Click to filter">
            <div className="lbl">{k.label}</div>
            <div className="val" style={{ color: k.color }}>{k.val}</div>
            <div className="sub" style={{ color: k.color, opacity: .7 }}>{k.sub}</div>
          </div>
        ))}
      </div>

      {/* Row 1: Active Vacancies full width */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <div className="sec-title" style={{ margin: 0 }}>⚑ Active Vacancy Searches</div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button className="btn btn-ghost btn-sm" onClick={() => navigate('/vacancy')}>All →</button>
            {canCreateVacancy ? <button className="btn btn-primary btn-sm" onClick={() => navigate('/vacancy?new=1')}>+ New Search</button> : null}
          </div>
        </div>
        <div id="dash-vacancies">
          {!activeVacancies || activeVacancies.open.length === 0 ? (
            <div style={{ color: 'var(--muted)', fontSize: 11, textAlign: 'center', padding: 16 }}>
              No active vacancy searches.
              <button className="btn btn-ghost btn-xs" style={{ marginLeft: 6 }} onClick={() => navigate('/vacancy?new=1')}>+ Start one →</button>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 10 }}>
              {activeVacancies.open.slice(0, 6).map((v) => {
                const p = projects.find((x) => x.id === v.projectId);
                const top3 = v.candidates ? v.candidates.slice(0, 3) : [];
                const projCode = p?.code ?? v.projectCode ?? '';
                const projName = p?.name ?? v.projectName ?? '';
                return (
                  <div
                    key={v.id}
                    className="dash-vacancy-card"
                    onClick={() => navigate(`/vacancy?id=${v.id}`)}
                  >
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 7 }}>
                      <div>
                        <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 12 }}>{v.role}</div>
                        <div style={{ fontSize: 10, color: 'var(--accent)' }}>{projCode}{projCode && projName ? ' — ' : ''}{projName}</div>
                        <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 2 }}>Start: {new Date(v.startDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })} · {v.duration}</div>
                      </div>
                      <span style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 13, color: 'var(--live)' }}>{v.candidates?.length ?? 0}</span>
                    </div>
                    {v.requesterName ? (
                      <div style={{ fontSize: 9, color: 'var(--info)', marginBottom: 6 }}>👤 {v.requesterName}</div>
                    ) : null}
                    {top3.length ? (
                      <div style={{ display: 'flex' }}>
                        {top3.map((w) => (
                          <div
                            key={w.workerId}
                            title={w.workerName}
                            style={{ width: 22, height: 22, borderRadius: '50%', background: 'var(--accent)', border: '2px solid var(--surface)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 8, fontWeight: 800, color: '#1E2328', marginRight: -6 }}
                          >
                            {initials(w.workerName)}
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          )}
          {activeVacancies && activeVacancies.open.length > 6 ? (
            <div style={{ textAlign: 'center', marginTop: 10 }}>
              <button className="btn btn-ghost btn-sm" onClick={() => navigate('/vacancy')}>View all {activeVacancies.open.length} vacancies →</button>
            </div>
          ) : null}
        </div>
      </div>

      {/* Row 2: Worker Availability Calendar */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <div className="sec-title" style={{ margin: 0 }}>🗓 Worker Availability</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <button className="btn btn-ghost btn-xs" onClick={calPrev}>‹</button>
            <span id="dash-cal-label" style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 11, minWidth: 110, textAlign: 'center' }}>{calLabel}</span>
            <button className="btn btn-ghost btn-xs" onClick={calNext}>›</button>
            <button className="btn btn-ghost btn-xs" onClick={() => navigate('/calendar')} style={{ marginLeft: 4 }}>Full view →</button>
          </div>
        </div>
        <div id="dash-cal-body" style={{ overflow: 'auto', maxHeight: 220 }}>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 8, fontSize: 9, color: 'var(--muted)' }}>
            {[
              { bg: 'var(--accent)', border: 'var(--accent)', label: 'On site' },
              { bg: 'rgba(29,78,216,.2)', border: '#1d4ed8', label: 'Deployed' },
              { bg: 'rgba(34,197,94,.22)', border: 'rgba(34,197,94,.4)', label: 'Available' },
              { bg: 'rgba(239,68,68,.2)', border: 'rgba(239,68,68,.4)', label: 'Unavailable' },
            ].map((s) => (
              <div key={s.label} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 9 }}>
                <div style={{ width: 12, height: 12, borderRadius: 3, background: s.bg, border: `1px solid ${s.border}`, flexShrink: 0 }}></div>
                {s.label}
              </div>
            ))}
          </div>
          <div style={{ overflow: 'auto', borderRadius: 7, border: '1px solid var(--border)' }}>
            <table style={{ borderCollapse: 'collapse', fontSize: 10, width: '100%' }}>
              <thead style={{ position: 'sticky', top: 0, zIndex: 2, background: 'var(--surface)', borderBottom: '1px solid var(--border)' }}>
                <tr>
                  <th style={{ textAlign: 'left', padding: '4px 8px', position: 'sticky', left: 0, background: 'var(--surface)', zIndex: 3, minWidth: 130, borderRight: '1px solid var(--border)', fontSize: 9 }}>Worker</th>
                  {Array.from({ length: daysInMonth }, (_, i) => {
                    const d = i + 1;
                    const date = new Date(calYear, calMonth, d);
                    const dow = (date.getDay() + 6) % 7;
                    const isToday = date.getTime() === today.getTime();
                    const isWeekend = dow >= 5;
                    return (
                      <th
                        key={d}
                        style={{ minWidth: 26, textAlign: 'center', padding: '2px 1px', fontSize: 8, opacity: isWeekend ? .4 : 1, background: isToday ? 'rgba(232,164,39,.12)' : undefined }}
                      >
                        <div>{dayNames[dow]}</div>
                        <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: isToday ? 800 : 400, fontSize: 9, color: isToday ? 'var(--accent)' : undefined }}>{d}</div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {workers.map((w) => (
                  <tr key={w.id}>
                    <td
                      style={{ whiteSpace: 'nowrap', padding: '3px 8px', position: 'sticky', left: 0, background: 'var(--surface)', zIndex: 1, minWidth: 130, borderRight: '1px solid var(--border)', cursor: 'pointer' }}
                      onClick={() => navigate(`/workers?id=${w.id}`)}
                    >
                      <div style={{ fontSize: 10, fontWeight: 600 }}>{w.name}</div>
                      <div style={{ fontSize: 8, color: 'var(--muted)' }}>{w.role}</div>
                    </td>
                    {Array.from({ length: daysInMonth }, (_, i) => {
                      const d = i + 1;
                      const date = new Date(calYear, calMonth, d);
                      const dateStr = date.toISOString().split('T')[0]!;
                      const dow = (date.getDay() + 6) % 7;
                      const isWeekend = dow >= 5;
                      const isToday = date.getTime() === today.getTime();
                      const deploys = deployments.filter((r) => r.workerId === w.id && r.status === 'active' && r.startDate.slice(0, 10) <= dateStr && (!r.endDate || r.endDate.slice(0, 10) >= dateStr));
                      const onSite = signinRecords.some((r) => r.workerId === w.id && r.type === 'in' && r.timestamp.slice(0, 10) === dateStr);
                      let dotBg = 'rgba(34,197,94,.22)', dotBorder = 'rgba(34,197,94,.4)', dotColor = '#22c55e', dot = '';
                      let onClickCell: (() => void) | undefined;
                      let tipText = 'Available';
                      if (w.status === 'unavailable' || w.status === 'dnu') {
                        dotBg = 'rgba(239,68,68,.2)'; dotBorder = 'rgba(239,68,68,.4)'; dotColor = '#ef4444'; dot = '✕';
                        tipText = 'Unavailable';
                      } else if (deploys.length) {
                        const pc = projectColour(deploys[0]!.projectId);
                        const projCode = projects.find((p) => p.id === deploys[0]!.projectId)?.code ?? '';
                        if (onSite) {
                          dotBg = pc.bg; dotBorder = pc.bg; dotColor = '#fff'; dot = '●';
                          tipText = `On site${projCode ? `: ${projCode}` : ''}`;
                        } else {
                          dotBg = pc.light; dotBorder = pc.bg; dotColor = pc.text; dot = '▶';
                          tipText = `Deployed${projCode ? `: ${projCode}` : ''}`;
                        }
                        onClickCell = () => navigate(`/deploylog?id=${deploys[0]!.id}`);
                      }
                      return (
                        <td
                          key={d}
                          style={{ textAlign: 'center', padding: 1, opacity: isWeekend ? .4 : 1, background: isToday ? 'rgba(232,164,39,.08)' : undefined }}
                        >
                          <div
                            title={`${w.name} · ${date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })} — ${tipText}`}
                            style={{ width: 20, height: 20, margin: '0 auto', borderRadius: 4, background: dotBg, border: `1.5px solid ${dotBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 8, fontWeight: 700, color: dotColor, outline: isToday ? '2px solid var(--accent)' : undefined, outlineOffset: isToday ? 1 : undefined, cursor: onClickCell ? 'pointer' : undefined }}
                            onClick={onClickCell}
                          >
                            {dot}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Row 3: Project Locations map full width */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Project Locations</div>
        <div className="map-wrap" id="dash-map">
          <div className="map-hdr">
            <span style={{ fontFamily: 'Syne, sans-serif', fontSize: 10, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--muted)' }}>Project Locations</span>
            <div style={{ display: 'flex', gap: 10, fontSize: 10, color: 'var(--muted)', alignItems: 'center' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><span className="dot dot-live"></span>Live</span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><span className="dot dot-completed"></span>Completed</span>
            </div>
          </div>
          <div className="map-body" ref={mapBodyRef} style={{ height: 200 }}>
            {mapPins.length === 0 ? (
              <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--muted)', fontSize: 11, zIndex: 2 }}>
                No projects with coordinates
              </div>
            ) : null}
            <RealMap
              pins={realMapPins}
              height="100%"
              onPinEnter={(id, cp) => {
                const pos = positionTooltip(cp, mapBodyRef.current);
                setHoverPin({ id, leftPx: pos.leftPx, topPx: pos.topPx });
              }}
              onPinLeave={() => setHoverPin(null)}
              onPinClick={(id) => navigate(`/projects?id=${id}`)}
            >
              {hoverPin && hoverProject ? (
                <div className="map-tt on" style={{ left: hoverPin.leftPx, top: hoverPin.topPx }}>
                  <div className="map-tt-title">{hoverProject.code}</div>
                  <div style={{ fontWeight: 500, marginBottom: 2, fontSize: 12 }}>{hoverProject.name}</div>
                  <div style={{ fontSize: 10, color: 'var(--muted)', marginBottom: 4 }}>{hoverProject.address ?? ''}</div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <span className={`badge b-${hoverProject.status === 'live' ? 'live' : 'completed'}`}>{hoverProject.status}</span>
                  </div>
                </div>
              ) : null}
            </RealMap>
          </div>
        </div>
      </div>

      {/* Row 4: Live projects + cert alerts + customers/compliance */}
      <div className="g2" style={{ marginBottom: 16 }}>
        <div>
          <div className="card" style={{ marginBottom: 12 }}>
            <div className="sec-title">Live Projects</div>
            <div id="dash-live">
              {liveProjects.map((p) => {
                const wc = workers.filter((w) => deployments.some((d) => d.workerId === w.id && d.projectId === p.id && d.status === 'active')).length;
                return (
                  <div
                    key={p.id}
                    className="dash-row"
                    onClick={() => navigate(`/projects?id=${p.id}`)}
                  >
                    <span className="dot dot-live"></span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 12, fontWeight: 500 }}>{p.name}</div>
                      <div style={{ fontSize: 10, color: 'var(--muted)' }}>{p.code} · {wc} worker{wc !== 1 ? 's' : ''}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="card">
            <div className="sec-title">Deployment Summary</div>
            <div id="dash-deploy-sum">
              {Object.keys(activeDepsByProject).length === 0 ? (
                <div style={{ fontSize: 11, color: 'var(--muted)' }}>No active deployments</div>
              ) : (
                Object.entries(activeDepsByProject).map(([pid, cnt]) => {
                  const p = projects.find((x) => x.id === pid);
                  return (
                    <div
                      key={pid}
                      className="dash-row"
                      onClick={() => navigate(`/deploylog?projectId=${pid}`)}
                    >
                      <span style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 10, color: 'var(--accent)', minWidth: 60 }}>{p?.code ?? '—'}</span>
                      <div style={{ flex: 1, fontSize: 11 }}>{p?.name ?? '—'}</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <div style={{ height: 4, borderRadius: 2, background: 'var(--live)', width: `${Math.min(cnt * 12, 60)}px` }}></div>
                        <span style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 11, color: 'var(--live)' }}>{cnt}</span>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
        <div>
          <div className="card" id="dash-availsignals-card" style={{ marginBottom: 12, display: recentSignals.length ? 'block' : 'none', borderLeft: '3px solid #22c55e' }}>
            <div className="sec-title" style={{ color: '#22c55e' }}>
              🟢 Newly Available
              <button className="btn btn-ghost btn-xs" style={{ marginLeft: 8, float: 'right' }} onClick={() => navigate('/availsignals')}>View All →</button>
            </div>
            <div id="dash-availsignals">
              {recentSignals.map((s) => {
                const w = workers.find((x) => x.id === s.workerId);
                return (
                  <div
                    key={s.id}
                    className="dash-row dash-row-green"
                    onClick={() => navigate(`/workers?id=${s.workerId}`)}
                  >
                    {w?.photo ? (
                      <div style={{ width: 24, height: 24, minWidth: 24, borderRadius: '50%', overflow: 'hidden', flexShrink: 0 }}>
                        <img src={w.photo} alt={s.workerName} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      </div>
                    ) : avEl(s.workerName, 24, 8)}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 11, fontWeight: 600 }}>{s.workerName}</div>
                      <div style={{ fontSize: 9, color: 'var(--muted)' }}>
                        {s.workerRole ?? ''}
                        {s.wasDeployedOn && s.wasDeployedOn.length ? ` · was on ${s.wasDeployedOn.join(', ')}` : ''}
                      </div>
                    </div>
                    <span style={{ fontSize: 9, color: '#22c55e', fontWeight: 600, whiteSpace: 'nowrap' }}>{availSignalAge(s.signaledAt)}</span>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="card" style={{ marginBottom: 12 }}>
            <div className="sec-title">
              Certificate Alerts
              <button className="btn btn-warn btn-xs" style={{ marginLeft: 8, float: 'right' }} onClick={() => setNotifyOpen(true)}>🔔 Notify All</button>
            </div>
            <div id="dash-certs">
              {certAlerts.length === 0 ? (
                <div style={{ fontSize: 11, color: 'var(--muted)' }}>No alerts</div>
              ) : (
                certAlerts.map((c) => (
                  <div
                    key={c.id}
                    className="dash-row dash-row-tight"
                    onClick={() => navigate(`/workers?id=${c.wid}`)}
                  >
                    {cBdg(c.status)}
                    <span style={{ flex: 1 }}>{c.wname} — {c.type}</span>
                    <span style={{ color: 'var(--muted)' }}>{c.expiry?.slice(0, 10) ?? ''}</span>
                  </div>
                ))
              )}
            </div>
          </div>
          <div className="g2">
            <div className="card">
              <div className="sec-title">Customers</div>
              <div id="dash-custs">
                {customers.map((c) => (
                  <div
                    key={c.id}
                    className="dash-row dash-row-tight"
                    onClick={() => navigate(`/customers?id=${c.id}`)}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 11, fontWeight: 500 }}>{c.name}</div>
                      <div style={{ fontSize: 10, color: 'var(--muted)' }}>{c.type}</div>
                    </div>
                    <span style={{ color: 'var(--accent)', fontFamily: 'Syne, sans-serif', fontWeight: 700 }}>
                      {c.projects.length} proj
                    </span>
                  </div>
                ))}
              </div>
            </div>
            <div className="card">
              <div className="sec-title">Compliance</div>
              <div id="dash-comp">
                {suppliers.map((s) => (
                  <div
                    key={s.id}
                    className="dash-row dash-row-tight"
                    onClick={() => navigate(`/suppliers?id=${s.id}`)}
                  >
                    {compBdg(s.compliance)}
                    <span style={{ fontSize: 11, flex: 1 }}>{s.name.split(' ').slice(0, 2).join(' ')}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Row 5: Cert Expiry Forecast + Recent Activity */}
      <div className="g2" style={{ marginBottom: 16 }}>
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 9 }}>
            <div className="sec-title" style={{ margin: 0 }}>⏱ Cert Expiry Forecast</div>
            <button className="btn btn-warn btn-xs" onClick={() => navigate('/training?expiring=1')}>View All →</button>
          </div>
          <div id="dash-expiry-forecast">
            {expiryForecast.length === 0 ? (
              <div style={{ color: 'var(--live)', fontSize: 11, textAlign: 'center', padding: 10 }}>✓ No certs expiring in 30 days</div>
            ) : (
              <>
                {expiryForecast.slice(0, 5).map((cv) => (
                  <div
                    key={cv.id}
                    className="dash-row dash-row-warn"
                    onClick={() => navigate(`/workers?id=${cv.wid}`)}
                  >
                    {cBdg(cv.status)}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 11, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cv.wname}</div>
                      <div style={{ fontSize: 9, color: 'var(--muted)' }}>{cv.type}</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 10, color: 'var(--warn)', fontFamily: 'Syne, sans-serif', fontWeight: 600 }}>{cv.daysLeft}d</div>
                    </div>
                  </div>
                ))}
                <button className="btn btn-ghost btn-xs" style={{ marginTop: 7, width: '100%', justifyContent: 'center' }} onClick={() => navigate('/training?expiring=1')}>
                  All {expiryForecast.length} expiring →
                </button>
              </>
            )}
          </div>
        </div>
        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 9 }}>
            <div className="sec-title" style={{ margin: 0 }}>Recent Activity</div>
            <button className="btn btn-ghost btn-sm" onClick={() => navigate('/deploylog')}>All →</button>
          </div>
          <div id="dash-activity">
            {activity.slice(0, 8).map((a) => (
              <div key={a.id} className="act-row">
                <div className="act-icon">{a.icon}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 11, lineHeight: 1.4 }}>{a.text}</div>
                  <div style={{ fontSize: 9, color: 'var(--faint)', marginTop: 2 }}>{relTime(new Date(a.time))}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      {notifyOpen ? <CertNotifyModal onClose={() => setNotifyOpen(false)} /> : null}
    </div>
  );
}
